import React, { useCallback, useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import useEmblaCarousel from "embla-carousel-react";
import AutoScroll from "embla-carousel-auto-scroll";

interface Book {
  id: number;
  title: string;
  cover_url: string | null;
  slug: string;
  author_name?: string | null;
}

interface AutoScrollCarouselProps {
  title: string;
  books: Book[];
  linkTo?: string;
  compact?: boolean;
  showAuthor?: boolean;
  speed?: number;
  hideArrows?: boolean;
}

const AutoScrollCarousel = ({
  title,
  books,
  linkTo,
  compact = false,
  showAuthor = false,
  speed = 1,
  hideArrows = false,
}: AutoScrollCarouselProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      dragFree: true,
      align: "start",
    },
    [
      AutoScroll({
        speed: speed,
        stopOnInteraction: true,
        stopOnMouseEnter: true,
        playOnInit: true,
        startDelay: 200,
      }),
    ],
  );

  const [isHovered, setIsHovered] = useState(false);
  const [canScroll, setCanScroll] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const isDraggingScrollbar = useRef(false);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  // Stop auto-scroll immediately
  const stopAutoScroll = useCallback(() => {
    const autoScroll = emblaApi?.plugins()?.autoScroll;
    if (autoScroll && typeof autoScroll.stop === "function") {
      autoScroll.stop();
    }
  }, [emblaApi]);

  // Resume auto-scroll
  const resumeAutoScroll = useCallback(() => {
    const autoScroll = emblaApi?.plugins()?.autoScroll;
    if (autoScroll && typeof autoScroll.play === "function") {
      autoScroll.play();
    }
  }, [emblaApi]);

  const handleMouseEnter = useCallback(() => {
    if (isDraggingScrollbar.current) return;
    setIsHovered(true);
    stopAutoScroll();
  }, [stopAutoScroll]);

  const handleMouseLeave = useCallback(() => {
    if (isDraggingScrollbar.current) return;
    setIsHovered(false);
    resumeAutoScroll();
  }, [resumeAutoScroll]);

  // Update scroll progress from Embla and check if scrolling is possible
  useEffect(() => {
    if (!emblaApi) return;

    const onScroll = () => {
      if (isDraggingScrollbar.current) return;
      const progress = emblaApi.scrollProgress();
      setScrollProgress(Math.max(0, Math.min(1, progress)));
    };

    const checkCanScroll = () => {
      // Check if there are more items than can fit in the viewport
      const canScrollPrev = emblaApi.canScrollPrev();
      const canScrollNext = emblaApi.canScrollNext();
      setCanScroll(canScrollPrev || canScrollNext);
    };

    emblaApi.on("scroll", onScroll);
    emblaApi.on("reInit", checkCanScroll);
    emblaApi.on("resize", checkCanScroll);
    
    onScroll();
    checkCanScroll();

    return () => {
      emblaApi.off("scroll", onScroll);
      emblaApi.off("reInit", checkCanScroll);
      emblaApi.off("resize", checkCanScroll);
    };
  }, [emblaApi]);

  // Handle scrollbar drag with immediate response
  const handleScrollbarMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!emblaApi || !scrollContainerRef.current) return;

      e.preventDefault();
      
      // 1. IMMEDIATE STOP: Kill auto-scroll instantly on mousedown
      stopAutoScroll();
      isDraggingScrollbar.current = true;

      // 2. PREVENT TEXT SELECTION during drag
      document.body.style.userSelect = "none";
      document.body.style.cursor = "grabbing";

      const rect = scrollContainerRef.current.getBoundingClientRect();
      const scrollSnaps = emblaApi.scrollSnapList();
      const totalSnaps = scrollSnaps.length;

      // Calculate and scroll to position immediately
      const scrollToPosition = (clientX: number) => {
        const currentRect = scrollContainerRef.current?.getBoundingClientRect() || rect;
        const position = Math.max(0, Math.min(1, (clientX - currentRect.left) / currentRect.width));
        const targetIndex = Math.round(position * (totalSnaps - 1));
        
        // 3. INSTANT RESPONSIVENESS: Use jump=true for no animation lag
        emblaApi.scrollTo(targetIndex, true);
        setScrollProgress(position);
      };

      // Scroll immediately on mousedown
      scrollToPosition(e.clientX);

      const handleMouseMove = (moveEvent: MouseEvent) => {
        moveEvent.preventDefault();
        scrollToPosition(moveEvent.clientX);
      };

      const handleMouseUp = () => {
        isDraggingScrollbar.current = false;

        // Restore text selection and cursor
        document.body.style.userSelect = "";
        document.body.style.cursor = "";

        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
        // Important UX: do NOT resume auto-scroll here.
        // Auto-scroll will only resume on mouse leave (handleMouseLeave),
        // ensuring it never "fights" the scrollbar, even at the edges.
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    },
    [emblaApi, stopAutoScroll, resumeAutoScroll],
  );

  if (!books || books.length === 0) return null;

  const thumbWidth = Math.max(15, 100 / books.length);

  return (
    <div className={compact ? "mb-10" : "mb-12"}>
      {(title || linkTo) && (
        <div className="flex items-center justify-between mb-5">
          {linkTo ? (
            <Link 
              to={linkTo} 
              className={`${compact ? "text-base md:text-lg" : "text-lg md:text-xl"} font-display font-medium text-foreground hover:text-primary transition-colors`}
            >
              {title}
            </Link>
          ) : (
            <h2 className={`${compact ? "text-base md:text-lg" : "text-lg md:text-xl"} font-display font-medium text-foreground`}>
              {title}
            </h2>
          )}
          <div className="flex items-center gap-2">
            {!hideArrows && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full hover:bg-muted"
                  onClick={scrollPrev}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full hover:bg-muted"
                  onClick={scrollNext}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </>
            )}
            {linkTo && (
              <Link
                to={linkTo}
                className="text-primary hover:text-primary/80 flex items-center gap-1 text-sm font-medium transition-colors ml-2"
              >
                View all
                <ChevronRight className="h-4 w-4" />
              </Link>
            )}
          </div>
        </div>
      )}

      <div className="relative px-1" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex -ml-5 md:-ml-6">
            {books.map((book) => (
              <div
                key={book.id}
                className={`flex-none pl-5 md:pl-6 ${compact ? "w-[48%] sm:w-[32%] md:w-[24%] lg:w-[19%]" : "w-[55%] sm:w-[40%] md:w-[28%] lg:w-[22%]"}`}
              >
                <Link to={book.slug} className="group/card block">
                  <Card className="overflow-hidden border border-border/40 shadow-sm hover:shadow-md transition-all duration-300 bg-card rounded-lg">
                    <div className="aspect-[3/4] overflow-hidden bg-muted relative rounded-t-lg">
                      {book.cover_url ? (
                        <img
                          src={book.cover_url}
                          alt={book.title}
                          width={250}
                          height={333}
                          className="h-full w-full object-cover transition-transform duration-300 group-hover/card:scale-105"
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center text-muted-foreground text-xs p-3 text-center">
                          {book.title}
                        </div>
                      )}
                    </div>
                    <CardContent className="p-3 bg-card">
                      <h3 className="font-medium text-foreground text-xs leading-snug line-clamp-2 whitespace-normal group-hover/card:text-primary transition-colors min-h-[2rem]">
                        {book.title}
                      </h3>
                      {showAuthor && book.author_name && (
                        <p className="text-[10px] text-muted-foreground mt-1.5 line-clamp-1">{book.author_name}</p>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* Custom scrollbar - only visible on hover AND when scrolling is possible */}
        {canScroll && (
          <div
            ref={scrollContainerRef}
            className={`mt-5 h-1.5 bg-muted rounded-full cursor-grab active:cursor-grabbing transition-opacity duration-300 ${
              isHovered ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
            onMouseDown={handleScrollbarMouseDown}
          >
            <div
              className="h-full bg-primary/50 hover:bg-primary/70 rounded-full pointer-events-none"
              style={{
                width: `${thumbWidth}%`,
                marginLeft: `${scrollProgress * (100 - thumbWidth)}%`,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AutoScrollCarousel;
